import { useState, type ReactNode } from 'react'
import { apiErrorMessage, displayName, findByName, planSave, type LayoutSummary, type SaveTarget } from '../../lib/dashboardLayouts'

type Mode = 'save' | 'overwrite' | 'new'

/**
 * The Save Layout dialog shared by all four dashboards. Its plain Save writes to the layout on
 * screen — or to My layout while the system default is showing — so most people never need more;
 * Overwrite and Save as new serve anyone keeping named layouts. Each page passes its own admin
 * section in as children, unchanged. Like every dimming modal it closes only from its buttons.
 */
export default function SaveLayoutDialog({ layouts, currentLayoutId, initialError = null, onSave, onClose, children }: {
    /** The user's own saved layouts for this dashboard */
    layouts: LayoutSummary[]
    currentLayoutId: string | null
    initialError?: string | null
    /** Rejects when the save fails; the dialog then stays open and says why */
    onSave: (target: SaveTarget) => Promise<void>
    onClose: () => void
    children?: ReactNode
}) {
    const plan = planSave(layouts, currentLayoutId)
    const others = layouts.filter(l => l.id !== plan.target.id)

    const [mode, setMode] = useState<Mode>('save')
    const [overwriteId, setOverwriteId] = useState<string | null>(null)
    const [newName, setNewName] = useState('')
    // null follows the target: an overwritten layout keeps its own default flag, a new one starts without
    const [makeDefault, setMakeDefault] = useState<boolean | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(initialError)

    const overwriteTarget = others.find(l => l.id === overwriteId) ?? others[0] ?? null
    const clash = findByName(layouts, newName)

    const target: SaveTarget | null =
        mode === 'save' ? plan.target
        : mode === 'overwrite' ? overwriteTarget && { id: overwriteTarget.id, name: displayName(overwriteTarget), isDefault: makeDefault ?? overwriteTarget.isDefault }
        : newName.trim() && !clash ? { id: null, name: newName.trim(), isDefault: makeDefault ?? false }
        : null

    const choose = (next: Mode) => {
        setMode(next)
        setMakeDefault(null)
    }

    const overwriteInstead = () => {
        if (!clash) return
        if (clash.id === plan.target.id) choose('save')
        else {
            setOverwriteId(clash.id)
            choose('overwrite')
        }
    }

    const submit = async () => {
        if (!target || saving) return
        setSaving(true)
        setError(null)
        try {
            await onSave(target)   // the caller closes the dialog once it has saved
        } catch (e) {
            setError(apiErrorMessage(e))
            setSaving(false)
        }
    }

    const buttonLabel = saving ? 'Saving…'
        : mode === 'overwrite' ? 'Overwrite'
        : mode === 'new' ? 'Save as new'
        : plan.kind === 'replace-mine' ? 'Replace' : 'Save'

    return (
        <div className="admin-save-overlay">
            <div className="admin-save-dialog save-layout-dialog" role="dialog" aria-modal="true" aria-labelledby="save-layout-title">
                <h3 id="save-layout-title">Save Layout</h3>

                <div className="save-layout-options">
                    <div className="save-layout-option">
                        <label>
                            <input type="radio" name="save-layout-mode" checked={mode === 'save'} onChange={() => choose('save')} />
                            {plan.kind === 'current' ? <>Save <strong>{plan.target.name}</strong></>
                                : plan.kind === 'replace-mine' ? <>Replace <strong>{plan.target.name}</strong></>
                                : <>Save as <strong>{plan.target.name}</strong></>}
                            {plan.kind === 'current' && plan.target.isDefault && <span className="presets-badge">Default</span>}
                        </label>
                    </div>
                    {plan.kind === 'create-mine' && (
                        <div className="save-layout-hint">You're on the system default. This keeps it as your own layout, the one this dashboard opens with.</div>
                    )}
                    {plan.kind === 'replace-mine' && (
                        <div className="save-layout-hint">You're on the system default. Saving puts it in place of your own layout.</div>
                    )}

                    {others.length > 0 && (
                        <div className="save-layout-option">
                            <label>
                                <input type="radio" name="save-layout-mode" checked={mode === 'overwrite'} onChange={() => choose('overwrite')} />
                                Overwrite
                            </label>
                            <select
                                aria-label="Layout to overwrite"
                                value={overwriteTarget?.id ?? ''}
                                onChange={e => { setOverwriteId(e.target.value); choose('overwrite') }}
                            >
                                {others.map(l => (
                                    <option key={l.id} value={l.id}>{displayName(l)}{l.isDefault ? ' (default)' : ''}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="save-layout-option">
                        <label>
                            <input type="radio" name="save-layout-mode" checked={mode === 'new'} onChange={() => choose('new')} />
                            Save as new
                        </label>
                        <input
                            type="text"
                            aria-label="New layout name"
                            placeholder="Layout name"
                            value={newName}
                            onFocus={() => mode !== 'new' && choose('new')}
                            onChange={e => { setNewName(e.target.value); if (mode !== 'new') choose('new') }}
                            onKeyDown={e => { if (e.key === 'Enter') submit() }}
                        />
                    </div>
                    {mode === 'new' && clash && (
                        <div className="save-layout-hint save-layout-clash">
                            You already have a layout called <strong>{displayName(clash)}</strong>.{' '}
                            <button type="button" className="link-btn" onClick={overwriteInstead}>Overwrite it instead</button>
                        </div>
                    )}
                </div>

                {mode !== 'save' && (
                    <label className="admin-save-checkbox">
                        <input type="checkbox" checked={target?.isDefault ?? makeDefault ?? false} onChange={e => setMakeDefault(e.target.checked)} />
                        Open this layout by default
                    </label>
                )}

                {error && <div className="layout-ie-error save-layout-error" role="alert">{error}</div>}

                <div className="admin-save-actions" style={{ marginTop: 'var(--space-3)' }}>
                    <button className="btn btn-primary" onClick={submit} disabled={!target || saving}>{buttonLabel}</button>
                </div>

                {children}

                <div className="admin-save-actions" style={{ marginTop: 'var(--space-2)' }}>
                    <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                </div>
            </div>
        </div>
    )
}
